import { Box, Button, Grid, IconButton, Stack, TextField } from '@mui/material'
import { CompanyType } from '@planning/app/api'
import { CompanyAutocomplete } from '@planning/components/autocompletes/company-autocomplete/CompanyAutocomplete'
import {
  VesselVisitGeneralInformationFormStore,
  VoyageType,
} from '@planning/pages/VesselVisit/Stores/VesselVisitGeneralInformationFormStore'
import { useTranslate } from '@tolgee/react'
import { BinIcon, PlusIcon } from '@tom-ui/ui'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { SectionSubtitle } from '../../atoms/SectionSubtitle'

type VoyageGroupProps = {
  index: number
  voyage: VoyageType
  operatorRequired?: boolean
  onDelete?: () => void
}

const VoyageGroup: FC<VoyageGroupProps> = observer(props => {
  const { index, voyage, operatorRequired, onDelete } = props
  const { t } = useTranslate()

  return (
    <Box data-cy={`vessel-visit-voyage-group-${index}`}>
      <Box display='flex' alignItems='center' justifyContent='space-between'>
        <SectionSubtitle label={t('voyageCount', 'Voyage no. {count}', { count: index + 1 })} />
        {onDelete && (
          <IconButton
            color='default'
            onClick={() => onDelete()}
            data-cy='vessel-visit-delete-voyage-btn'
          >
            <BinIcon />
          </IconButton>
        )}
      </Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6 }}>
          <TextField
            {...voyage.inboundNumber.props}
            required
            onBlur={voyage.inboundNumber.validate}
            fullWidth
            label={`${t('inboundNumberAbrv', 'Inbound no.')}`}
            variant='outlined'
            type='text'
            name={`inboundNumber`}
            data-cy='vessel-visit-inbound-number'
            error={voyage.inboundNumber.isTouched && !!voyage.inboundNumber.error}
            helperText={
              voyage.inboundNumber.isTouched && !!voyage.inboundNumber.error
                ? t('fieldIsRequired', 'Field is required.')
                : ''
            }
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <TextField
            {...voyage.outboundNumber.props}
            required
            onBlur={voyage.outboundNumber.validate}
            fullWidth
            label={`${t('outboundNumberAbrv', 'Outbound no.')}`}
            variant='outlined'
            type='text'
            name={`outboundNumber`}
            data-cy='vessel-visit-outbound-number'
            error={voyage.outboundNumber.isTouched && !!voyage.outboundNumber.error}
            helperText={
              voyage.outboundNumber.isTouched && !!voyage.outboundNumber.error
                ? t('fieldIsRequired', 'Field is required.')
                : ''
            }
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <CompanyAutocomplete
            required={operatorRequired}
            label={t('shippingLine', 'Shipping line')}
            selectedCustomer={voyage.shippingLine.value}
            validate={voyage.shippingLine.validate}
            handleOnChange={customer => {
              voyage.shippingLine.setValue(customer)
            }}
            dataCy='vessel-visit-shipping-line'
            error={!!voyage.shippingLine.error}
            helperText={voyage.shippingLine.error ? t(voyage.shippingLine.error) : ''}
            filterByTypes={[CompanyType.ShippingLine]}
          />
        </Grid>
      </Grid>
    </Box>
  )
})

type VesselVisitVoyageSubFormProps = { store: VesselVisitGeneralInformationFormStore }

export const VesselVisitVoyageSubForm = observer((props: VesselVisitVoyageSubFormProps) => {
  const { store } = props

  const { t } = useTranslate()

  const hasMultipleVoyages = computed(() => store.voyages.value.length > 1).get()

  return (
    <Stack gap={1} marginTop={1.5}>
      {store.voyages.value.map((voyage: VoyageType, index: number) => (
        <VoyageGroup
          key={index}
          index={index}
          voyage={voyage}
          operatorRequired={hasMultipleVoyages}
          onDelete={
            index > 0
              ? () => {
                  store.removeVoyage(index)
                }
              : undefined
          }
        />
      ))}
      <Box>
        <Button
          type='button'
          variant='text'
          color='primary'
          onClick={() => {
            store.addVoyage()
          }}
          startIcon={<PlusIcon />}
          data-cy='vessel-visit-add-voyage-btn'
        >
          {t('add', 'Add')}
        </Button>
      </Box>
    </Stack>
  )
})
